'use strict';

import { extendDefaults } from '../utils/common';
import { startVisibleTimer } from '../utils/visibleTimer';
import EventBus from '../components/EventBus';

// The SDK gives an ad call 120s before it reports "Timeout: No message received
// from iframe.". AdSense is free to never call a single one of its callbacks —
// it happens when a fullscreen ad is torn down without `adBreakDone` — so the
// flow needs its own deadline, and it has to fire first: whoever settles first
// decides what the game sees, and a plain `{success:false}` beats an exception
// the game has no way to tell apart from a real ad failure.
//
// It is a silence detector, not a cap on how long an ad may take: the clock
// only runs while the tab is visible, and every sign of life below re-arms it.
// A fixed deadline killed rewarded ads the player had merely paused by
// switching tabs, and the reward was gone before they came back (#139).
const AD_FLOW_TIMEOUT_MS = 90000;
// Each of these means AdSense is still working the break: an ad started, ended,
// or paid out. AdInstance broadcasts them on the same (singleton) bus.
const AD_LIVENESS_EVENTS = [
  'IMPRESSION',
  'COMPLETE',
  'CONTENT_RESUME_REQUESTED',
  'SDK_REWARDED_WATCH_COMPLETE',
];
const TIMED_OUT = {
  message: 'Ad flow timed out waiting for the ad network.',
  status: 'error',
};

class AdFlow {
  constructor(e) {
    this._gameData = e._gameData;
    this._adInstance = e.adInstance;
    this._eventBus = e.eventBus;
    this.blockedAdsMap = e.blockedAdsMap;
    this._parentURL = e._parentURL;
    this._parentDomain = e._parentDomain;
    this._onResumeGame = e.onResumeGame.bind(e);
    // this._onPauseGame = e.onPauseGame.bind(e);
    // this._onRewardedWatchComplete = e.onRewardedWatchComplete.bind(e);
    // this._onAdError = e.onAdError.bind(e);
    // this._setAdRequestTimer = e._setAdRequestTimer.bind(e);
    // this._giveGift = e._giveGift.bind(e);
    // this._runAutoResume = e._runAutoResume.bind(e);
    // this.get_session_id = e.get_session_id.bind(e);
    // this.get_ad_system_init_result = e.get_ad_system_init_result.bind(e);
    this.eventBus = new EventBus();
  }
  async runAdFlow(t) {
    this.adRequest = t;
    t.params.adapter = 'adsense';
    t.params = extendDefaults({}, t.params);
    t.params = extendDefaults(t.params, {
      adapterIndex: 0,
      isLastAdapter: false,
      shouldFail: false,
      extraPromoRunOnFail: false,
    });
    var result = await this.adInvoke(t);
    if (result.success) {
      this._onResumeGame('Advertisement(s) are done. Start / resume the game.', 'success');
    } else {
      this._onResumeGame('DisplayAd failed.', 'warning');
    }
    return result;
  }
  async adInvoke(s) {
    var self = this;
    return new Promise((resolve) => {
      var scopeName = `main.showad.${s.params.adapter}.${s.params.adapterIndex}`;
      var settled = false;
      var watchdog = null;
      var timedOut = false;

      // Every exit runs through here so the caller always gets an answer, and
      // gets it once. `ownsAd` is false only when another flow already holds
      // the ad slot: clearing AdInstance's flag there would free a slot this
      // flow never took, and the next showAd would push an ad on top of the
      // one still playing.
      var settle = function (success, args, ownsAd) {
        if (settled) return;
        settled = true;
        if (watchdog) watchdog.cancel();
        self._eventBus.unsubscribeScope(scopeName);
        // AdInstance clears this only from inside `adBreakDone`, which AdSense
        // is free never to call.
        if (ownsAd !== false) self._adInstance.resetForNext();
        resolve({
          success: success,
          args: timedOut ? TIMED_OUT : args,
        });
      };
      var onFailure = function (args) {
        settle(false, args);
      };
      var onAdSuccess = function (args) {
        settle(true, args);
      };

      watchdog = startVisibleTimer(AD_FLOW_TIMEOUT_MS, function () {
        timedOut = true;
        // Tear the orphaned ad down before settling: `cancel()` hides the
        // overlay and resets the ads loader, but returns early once
        // `requestRunning` is false — so it has to run before settle() clears
        // that flag. Its AD_SDK_CANCELED lands on onFailure below, and
        // `timedOut` keeps the reported reason the timeout rather than the
        // cancel this function just caused.
        self._adInstance.cancel();
        settle(false, TIMED_OUT);
      });

      AD_LIVENESS_EVENTS.forEach(function (eventName) {
        self._eventBus.subscribe(
          eventName,
          function () {
            if (watchdog) watchdog.restart();
          },
          scopeName,
        );
      });

      self._eventBus.subscribe('AD_ERROR', onFailure, scopeName);
      self._eventBus.subscribe('AD_SDK_CANCELED', onFailure, scopeName);
      // AdInstance bails out with this one when `requestRunning` is still set;
      // without a listener the flow used to hang until the SDK timed out.
      self._eventBus.subscribe(
        'AD_IS_ALREADY_RUNNING',
        function (args) {
          settle(false, args, false);
        },
        scopeName,
      );
      self._eventBus.subscribe('AD_SUCCESS', onAdSuccess, scopeName);

      self._adInstance.startAd(s).catch(function (error) {
        settle(false, {
          message: (error && error.message) || String(error),
          status: 'error',
        });
      });
    });
  }
}

export default AdFlow;
